import type { SVGProps } from "react";

/**
 * Starsach's braille "S" (dots 2, 3, 4 on the 1 4 / 2 5 / 3 6 cell), the
 * Gairm library's product geometry (Marketing/Logo/Products, "Starsach
 * Braille Icon", 1 Oct 2026): r 37 on a 112 pitch in the 400 square, the
 * same grid as Gairm's "G". `currentColor` so the caller decides; the brand
 * orange is `text-brand`.
 */
export function StarsachMark(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 400 400" aria-hidden="true" focusable="false" fill="currentColor" {...props}>
      <circle cx="144" cy="200" r="37" />
      <circle cx="144" cy="312" r="37" />
      <circle cx="256" cy="88" r="37" />
    </svg>
  );
}

/**
 * The full logo: the braille cell as tall as the capitals, then "Starsach"
 * (the library's "Starsach Lockup", 1048 x 280). The dots are always the
 * brand orange; the name takes the text color, so the caller's
 * `text-ink dark:text-white` gives the light and dark lockups. Size it by
 * height.
 */
export function StarsachLockup({ className = "", title = "Starsach" }: { className?: string; title?: string }) {
  return (
    <svg viewBox="0 0 1048 280" role="img" aria-label={title} className={className} fill="currentColor">
      <g className="fill-brand">
        <circle cx="57.8" cy="111.7" r="17.8" />
        <circle cx="57.8" cy="165.6" r="17.8" />
        <circle cx="111.7" cy="57.8" r="17.8" />
      </g>
      <text x="198" y="183" fontFamily="'Helvetica Neue', Helvetica, Arial, sans-serif" fontSize="200" fontWeight="600" letterSpacing="-4">
        Starsach
      </text>
    </svg>
  );
}

/** Gairm's braille "G" (dots 1, 2, 4, 5), as in gairm-npo-bpo/marketing/logo. */
export function GairmMark(props: SVGProps<SVGSVGElement>) {
  return (
    <svg viewBox="0 0 400 400" aria-hidden="true" focusable="false" fill="currentColor" {...props}>
      <circle cx="110" cy="110" r="59" />
      <circle cx="110" cy="290" r="59" />
      <circle cx="290" cy="110" r="59" />
      <circle cx="290" cy="290" r="59" />
    </svg>
  );
}

/** The full logo in the rail and header; `as` picks the element around it. */
export function StarsachWordmark({ as: Tag = "span", className = "" }: { as?: "span" | "h1"; className?: string }) {
  return (
    <Tag className={`inline-flex items-center ${className}`}>
      <StarsachLockup className="h-7 w-auto" />
    </Tag>
  );
}

/** Small and quiet, on every page: who makes this. */
export function PoweredBy({ className = "" }: { className?: string }) {
  return (
    <p className={`text-xs text-neutral-500 dark:text-neutral-400 ${className}`}>
      Powered by{" "}
      <a href="https://gairm.org" className="whitespace-nowrap font-medium hover:text-neutral-700 dark:hover:text-neutral-200" target="_blank" rel="noreferrer">
        <GairmMark className="mr-1 inline-block h-3 w-3 align-[-0.125em] text-brand" />
        Gairm
      </a>
      , an Idaho non-profit.
    </p>
  );
}
